import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { TextInput } from 'react-scale/components/design-system';
import { makeStyles } from 'react-scale/components/design-system/styles';
import BuilderHint from 'react-scale/corp/components/QM/taskTemplatesEditor/BuilderHint';
const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
  },
  editor: {
    width: '100%',
    position: 'relative',
    '&:hover $formatButtonsDiv $prettifyButton, &:hover $formatButtonsDiv $savedTag': {
      opacity: 1,
    },
  },
  formatButtonsDiv: {
    position: 'absolute',
    right: 8,
    top: 8,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  prettifyButton: {
    background: '#f2f2f2',
    outline: 'none',
    border: 'none',
    padding: '4px 8px',
    fontSize: 12,
    borderRadius: 4,
    cursor: 'pointer',
    opacity: 0,
    transition: 'all 150ms ease-out',
    '&:disabled': {
      color: '#ccc',
      cursor: 'not-allowed',
    },
    '&:hover': {
      opacity: 1,
      background: '#e6e6e6',
    },
  },
  savedTag: {
    outline: 'none',
    border: 'none',
    padding: '4px 8px',
    fontSize: 12,
    borderRadius: 8,
    opacity: 0,
    transition: 'all 150ms ease-out',
  },
  savedTagSaved: {
    background: '#f6ffed',
    color: '#52c41a',
  },
  savedTagUnsaved: {
    background: '#fff1f0',
    color: '#ff4d4f',
  },
});

interface JSONInputBuilderProps {
  name: string;
  updateProperty: (value: any) => void;
  existingValue?: any;
  required?: boolean;
  hint?: string;
  disabled?: boolean;
  showSavedTag?: boolean;
}

const JSONInputBuilder: React.FC<JSONInputBuilderProps> = ({
  name,
  updateProperty,
  existingValue,
  required,
  hint,
  disabled,
  showSavedTag,
}: JSONInputBuilderProps) => {
  const classes = useStyles();
  const [rawJson, setRawJson] = useState<string>(JSON.stringify(existingValue) || '');
  const [isJsonValid, setIsJsonValid] = useState<boolean>(true);

  useEffect(() => {
    setRawJson(JSON.stringify(existingValue) || '');
  }, [existingValue]);

  const onEditRawJSON = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    try {
      const text = e.target.value;
      setRawJson(text);
      if (text !== '') {
        JSON.parse(text);
      }
      setIsJsonValid(true);
    } catch (error) {
      setIsJsonValid(false);
    }
  };

  const prettify = () => {
    try {
      if (rawJson !== '') {
        const obj = JSON.parse(rawJson);
        const pretty = JSON.stringify(obj, undefined, 2);
        setRawJson(pretty);
      }
      setIsJsonValid(true);
    } catch (error) {
      setIsJsonValid(false);
    }
  };

  return (
    <>
      <div className={classes.container}>
        <div className={classes.editor}>
          <TextInput
            value={rawJson}
            fullWidth
            multiline
            size="large"
            disabled={!!disabled}
            label={name}
            required={required}
            onChange={onEditRawJSON}
            onBlur={() => {
              try {
                if (rawJson === '') {
                  updateProperty(undefined);
                } else {
                  updateProperty(JSON.parse(rawJson));
                }
                setIsJsonValid(true);
              } catch (error) {
                updateProperty(undefined);
                setIsJsonValid(false);
              }
            }}
          />
          <div className={classes.formatButtonsDiv}>
            <button className={classes.prettifyButton} onClick={prettify} disabled={!isJsonValid}>
              Format
            </button>
            {showSavedTag && (
              <div
                className={clsx(
                  classes.savedTag,
                  isJsonValid ? classes.savedTagSaved : classes.savedTagUnsaved,
                )}
              >
                {isJsonValid ? 'Saved' : 'Not saved'}
              </div>
            )}
          </div>
        </div>
        {hint && <BuilderHint name={name} hint={hint} />}
      </div>
      {!isJsonValid && <span style={{ color: 'red' }}>The JSON is not valid.</span>}
    </>
  );
};

export default JSONInputBuilder;
